/**
 * CalendarSidebar
 * Sidebar with mini-calendar, upcoming events, and quick filters
 */

import React, { useState } from 'react'
import { Calendar, ChevronLeft, ChevronRight, Clock, Tag } from 'lucide-react'
import type { CalendarItem } from '../../types/calendar'
import { Button } from '@/components/ui/button'
import { cn } from '@/utils/cn'

interface CalendarSidebarProps {
  currentDate: Date
  onDateSelect: (date: Date) => void
  upcomingItems?: CalendarItem[]
}

/**
 * CalendarSidebar Component
 */
export const CalendarSidebar: React.FC<CalendarSidebarProps> = ({
  currentDate,
  onDateSelect,
  upcomingItems = []
}) => {
  const [miniCalendarDate, setMiniCalendarDate] = useState(new Date(currentDate))

  // Generate mini-calendar grid
  const generateMiniCalendar = () => {
    const year = miniCalendarDate.getFullYear()
    const month = miniCalendarDate.getMonth()

    // First day of the month
    const firstDay = new Date(year, month, 1)
    const firstDayOfWeek = firstDay.getDay()

    // Last day of the month
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()

    // Generate days
    const days: (number | null)[] = []

    // Add empty slots for days before the first day
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(null)
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day)
    }

    return days
  }

  const miniCalendarDays = generateMiniCalendar()

  const goToPreviousMonth = () => {
    const newDate = new Date(miniCalendarDate)
    newDate.setMonth(miniCalendarDate.getMonth() - 1)
    setMiniCalendarDate(newDate)
  }

  const goToNextMonth = () => {
    const newDate = new Date(miniCalendarDate)
    newDate.setMonth(miniCalendarDate.getMonth() + 1)
    setMiniCalendarDate(newDate)
  }

  const handleDayClick = (day: number) => {
    const selectedDate = new Date(
      miniCalendarDate.getFullYear(),
      miniCalendarDate.getMonth(),
      day
    )
    onDateSelect(selectedDate)
  }

  const isToday = (day: number) => {
    const today = new Date()
    return (
      day === today.getDate() &&
      miniCalendarDate.getMonth() === today.getMonth() &&
      miniCalendarDate.getFullYear() === today.getFullYear()
    )
  }

  const isSelected = (day: number) => {
    return (
      day === currentDate.getDate() &&
      miniCalendarDate.getMonth() === currentDate.getMonth() &&
      miniCalendarDate.getFullYear() === currentDate.getFullYear()
    )
  }

  const monthYearDisplay = miniCalendarDate.toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric'
  })

  return (
    <div className="flex w-full min-w-0 flex-col gap-6 rounded-lg border bg-card p-4 lg:w-80">
      <div>
        <h3 className="mb-4 flex items-center gap-2 font-semibold">
          <Calendar className="size-4 text-muted-foreground" aria-hidden="true" />
          Calendário
        </h3>

        <div className="mb-3 flex items-center justify-between">
          <Button variant="ghost" size="icon" className="size-7" onClick={goToPreviousMonth} aria-label="Mês anterior">
            <ChevronLeft className="size-4" />
          </Button>
          <span className="text-sm font-medium capitalize">{monthYearDisplay}</span>
          <Button variant="ghost" size="icon" className="size-7" onClick={goToNextMonth} aria-label="Próximo mês">
            <ChevronRight className="size-4" />
          </Button>
        </div>

        <div className="mb-2 grid grid-cols-7 gap-1">
          {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((day, index) => (
            <div key={index} className="text-center text-xs font-medium text-muted-foreground">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {miniCalendarDays.map((day, index) => (
            <div key={index} className="aspect-square">
              {day ? (
                <button
                  type="button"
                  onClick={() => handleDayClick(day)}
                  aria-current={isToday(day) ? 'date' : undefined}
                  className={cn(
                    'flex size-full items-center justify-center rounded text-xs tabular-nums transition-colors',
                    isSelected(day) && !isToday(day) && 'bg-primary font-semibold text-primary-foreground',
                    isToday(day) && 'bg-accent font-semibold text-accent-foreground ring-1 ring-primary/40',
                    !isToday(day) && !isSelected(day) && 'text-foreground hover:bg-muted'
                  )}
                >
                  {day}
                </button>
              ) : (
                <div />
              )}
            </div>
          ))}
        </div>
      </div>

      {upcomingItems && upcomingItems.length > 0 && (
        <div>
          <h3 className="mb-3 flex items-center gap-2 font-semibold">
            <Clock className="size-4 text-muted-foreground" aria-hidden="true" />
            Próximos Eventos
          </h3>

          <div className="flex flex-col gap-2">
            {upcomingItems.slice(0, 5).map((item) => (
              <div
                key={`${item.type}-${item.id}`}
                className="cursor-pointer rounded-md border p-2 hover:bg-muted"
              >
                <div className="flex items-start gap-2">
                  {item.type === 'event' ? (
                    <Calendar className="mt-0.5 size-3 shrink-0 text-muted-foreground" aria-hidden="true" />
                  ) : (
                    <Tag className="mt-0.5 size-3 shrink-0 text-muted-foreground" aria-hidden="true" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium">{item.title}</p>
                    <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                      <span>
                        {new Date(item.date).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: 'short'
                        })}
                      </span>
                      {item.startTime && <span>{item.startTime}</span>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <h3 className="mb-3 font-semibold">Legenda</h3>
        <div className="flex flex-col gap-2 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="h-3 w-4 rounded-sm border border-l-4 border-l-foreground/40 bg-card" />
            <span>Eventos</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-3 w-4 rounded-sm border-2 border-dashed bg-card" />
            <span>Tarefas</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-3 w-4 rounded-sm bg-destructive/20 ring-1 ring-destructive/40" />
            <span>Urgente</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-3 w-4 rounded-sm border border-foreground/40 bg-card" />
            <span>Alta Prioridade</span>
          </div>
        </div>
      </div>
    </div>
  )
}
